import type { OutputProfile } from '@/core/outputProfile';
import { api, apiRequest } from '@/core/http/apiClient';
import { download } from '@/core/http/files';
import { readBlobError } from '@/core/http/helpers';
import type {
  CompaniesHouseDataset,
  CompanyOfficerRow,
  CompanyPscRow,
  DataSecuritySettings,
  FilingHistoryRow,
  ImportIxbrlPayload,
  RefreshDirectorsResult,
  SubmissionDetail,
  SubmissionRow,
  SubmissionValidationResult,
  SubmitToCompaniesHousePayload,
} from '@/core/models/filing.models';

/** Accounts submissions, Companies House integrations and document data security. */

async function downloadFile(path: string): Promise<boolean> {
  try {
    download(await apiRequest<Blob>('GET', path, { responseType: 'blob' }));
    return true;
  } catch (error) {
    return readBlobError(error);
  }
}

export const filingService = {
  // ---- Accounts submissions ----

  getSubmissions: (clientId: string) => api.get<SubmissionRow[]>(`AccountsSubmission/getSubmissions/${clientId}`),

  getSubmission: (id: string) => api.get<SubmissionDetail>(`AccountsSubmission/getSubmission/${id}`),

  /** `outputProfile` names the Abridged or Filleted accounts to file; omitted files the Full accounts. */
  createSubmission: (
    clientId: string,
    accountingPeriodId: string | null = null,
    description: string | null = null,
    outputProfile: OutputProfile | null = null,
  ) =>
    api.post<SubmissionDetail>('AccountsSubmission/createSubmission', {
      clientId,
      accountingPeriodId,
      description,
      ...(outputProfile ? { outputProfile } : {}),
    }),

  importIxbrl: (payload: ImportIxbrlPayload) => {
    const form = new FormData();
    form.append('clientId', payload.clientId);
    form.append('accountingPeriodId', payload.accountingPeriodId);
    if (payload.description) {
      form.append('description', payload.description);
    }
    if (payload.fromDate) {
      form.append('fromDate', payload.fromDate);
    }
    if (payload.toDate) {
      form.append('toDate', payload.toDate);
    }
    if (payload.signDate) {
      form.append('signDate', payload.signDate);
    }
    form.append('file', payload.file, payload.file.name);
    return api.post<SubmissionDetail>('AccountsSubmission/importIxbrl', form);
  },

  deleteSubmission: (id: string) => api.post<boolean>(`AccountsSubmission/deleteSubmission/${id}`, {}),

  validateSubmission: (id: string) =>
    api.post<SubmissionValidationResult>(`AccountsSubmission/validateSubmission/${id}`, {}),

  submitSubmission: (id: string, payload: SubmitToCompaniesHousePayload) =>
    api.post<SubmissionDetail>(`AccountsSubmission/submitSubmission/${id}`, payload),

  downloadSubmission: (id: string) => downloadFile(`AccountsSubmission/downloadSubmission/${id}`),

  /** Asks Companies House for the decision on a pending submission and records it (the old app's clickable status). */
  refreshSubmissionStatus: (id: string) => api.post<SubmissionDetail>(`AccountsSubmission/refreshStatus/${id}`, {}),

  // ---- Companies House ----

  getOfficers: (clientId: string) =>
    api.get<CompaniesHouseDataset<CompanyOfficerRow>>(`CompaniesHouse/getOfficers/${clientId}`),

  getPersonsWithSignificantControl: (clientId: string) =>
    api.get<CompaniesHouseDataset<CompanyPscRow>>(`CompaniesHouse/getPersonsWithSignificantControl/${clientId}`),

  getFilingHistory: (clientId: string) =>
    api.get<CompaniesHouseDataset<FilingHistoryRow>>(`CompaniesHouse/getFilingHistory/${clientId}`),

  downloadFilingDocument: (clientId: string, transactionId: string) =>
    downloadFile(`CompaniesHouse/downloadFilingDocument/${clientId}/${encodeURIComponent(transactionId)}`),

  refreshAllDirectors: () => api.post<RefreshDirectorsResult>('CompaniesHouse/refreshAllDirectors', {}),

  // ---- Data security ----

  getDataSecurity: (clientId: string) => api.get<DataSecuritySettings>(`DataSecurity/getSettings/${clientId}`),

  saveDataSecurity: (clientId: string, isEnabled: boolean, masterPassword: string | null) =>
    api.post<DataSecuritySettings>('DataSecurity/saveSettings', { clientId, isEnabled, masterPassword }),
};
